import React, { useEffect, useState } from "react";
import { AlignRight, Plus, Check } from "lucide-react"; // Import Check icon for the Done button
import { useSearchParams } from "react-router-dom";

import * as Api from "@modules/api";
import PlaylistCard from "@components/items/display/PlaylistCard";
import TranslatedText from "@components/items/display/TranslatedText";
import { DialogTrigger } from "@ui/dialog";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import {
  arrangedplaylistItems,
  useAppDispatch,
  useAppSelector,
} from "@lib/stores";
import { getPlaylistByIdAsync } from "@lib/stores/myLibrary/slices";
import { ContentActions } from "@lib/stores/content/slices";
import AddItemToPlaylistDialog from "@components/layouts/b2c/my-corner/AddItemToPlaylistDialog";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import EmptyPlaylist from "@components/layouts/b2c/my-corner/EmptyPlaylist";
import {
  ItemContentOutputWithProgress,
  SelectedPlayerContentSource,
} from "@lib/stores/content/types";
import PlaylistItemCard from "@components/items/display/PlaylistItemCard";
import PlaylistItemCardSkeleton from "@components/items/display/PlaylistItemCardSkeleton";
import { useLockedContents } from "@modules/content/hooks/useLockedContent";
import { useContentSelector } from "@modules/content/hooks/useContentSelector";

const PlayListPage = () => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const playlistId = searchParams.get("id");

  const [isAddItemDialogOpen, setIsAddItemDialogOpen] =
    useState<boolean>(false);

  const playlistDetails = useAppSelector(
    (state) => state.myLibrary.playlistDetails.data
  );

  const playlistContentEntities = useAppSelector(
    (state) => state.content.playlistContentItems.data
  ) as ItemContentOutputWithProgress[];

  const playlistContentEntitiesStatus = useAppSelector(
    (state) => state.content.playlistContentItems.status
  );

  const addItemsToPlaylistStatus = useAppSelector(
    (state) => state.myLibrary.addItemToPlaylist.status
  );

  const [isDraggMode, setIsDraggMode] = useState<boolean>(false);
  const [draggedItemId, setDraggedItemId] = useState<string | undefined>(
    undefined
  );
  const [optimisticItems, setOptimisticItems] = useState<
    ItemContentOutputWithProgress[]
  >([]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const { isLockedContent } = useLockedContents(playlistContentEntities);
  const lockedContents = isLockedContent.result;
  const lockedContentsObj = lockedContents?.reduce(
    (prev, curr) => {
      prev[curr.id!] = curr;

      return prev;
    },
    {} as {
      [key: string]: {
        id: string | undefined;
        isLocked: boolean;
        lockType: Api.Server.LockType | undefined;
      };
    }
  );

  const { onSelectContent } = useContentSelector();

  useEffect(() => {
    if (playlistContentEntities) {
      setOptimisticItems(playlistContentEntities);
    }
  }, [playlistContentEntities]);

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    id: string | undefined
  ) => {
    if (id) {
      e.dataTransfer.setData("id", id);
      setDraggedItemId(id);
    }
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    id: string | undefined
  ) => {
    e.preventDefault();
    if (draggedItemId === undefined || draggedItemId === id) return;

    const draggedOverItemIndex = optimisticItems.findIndex(
      (item) => item.id === id
    );
    const draggedItemIndex = optimisticItems.findIndex(
      (item) => item.id === draggedItemId
    );

    if (draggedOverItemIndex === -1 || draggedItemIndex === -1) return;

    const newItems = [...optimisticItems];
    const [draggedItem] = newItems.splice(draggedItemIndex, 1);
    newItems.splice(draggedOverItemIndex, 0, draggedItem);

    setOptimisticItems(newItems);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedItemId(undefined);
  };

  const handleDoneClick = () => {
    if (!playlistId || !hasChanges) {
      setHasChanges(false);
      setIsDraggMode(false);
      return;
    }

    arrangedplaylistItems(
      {
        contentEntityIds: optimisticItems.map((item) => item.id) as
          | string[]
          | undefined,
        playlistId: parseInt(playlistId),
      },
      () => {
        dispatch(
          ContentActions.getContentEntityItemsByPlaylistIdAsync(
            parseInt(playlistId)
          )
        );
        setHasChanges(false);
        setIsDraggMode(false);
      }
    );
  };

  useEffect(() => {
    if (playlistId) {
      dispatch(getPlaylistByIdAsync(parseInt(playlistId)));
      dispatch(
        ContentActions.getContentEntityItemsByPlaylistIdAsync(
          parseInt(playlistId)
        )
      );
    }
  }, [playlistId, dispatch]);

  return (
    <div className="flex h-screen items-start justify-start gap-5 p-8">
      <PlaylistCard item={playlistDetails} />
      <div className="flex h-full w-full flex-col items-start justify-start">
        <div className="flex w-full items-start justify-between">
          <TranslatedText
            text={`my-library.${"list content"}`}
            className="text-[20px] font-medium text-[#999999]"
          />

          <div className="flex items-center justify-start gap-2">
            <AddItemToPlaylistDialog
              open={isAddItemDialogOpen}
              setOpen={setIsAddItemDialogOpen}
              trigger={
                <DialogTrigger className="flex h-6 w-6 items-center justify-center rounded-full bg-[#D9D9D9] text-white hover:opacity-80 dark:bg-[#3C3B40]">
                  <Plus size={20} />
                </DialogTrigger>
              }
            />
            {playlistDetails?.itemCount !== undefined &&
              playlistDetails.itemCount > 0 && (
                <>
                  <ShadcnButton
                    className={`h-6 w-6 rounded-full bg-[#D9D9D9] p-1 text-white hover:bg-[#D9D9D9] hover:opacity-80 dark:bg-[#3C3B40] dark:hover:bg-[#3C3B40] dark:hover:opacity-50 ${
                      isDraggMode &&
                      "bg-primary hover:bg-primary dark:bg-primary dark:hover:bg-primary"
                    }`}
                    onClick={() => setIsDraggMode(!isDraggMode)}
                  >
                    <AlignRight />
                  </ShadcnButton>
                  {isDraggMode && (
                    <ShadcnButton
                      className="h-6 w-6 rounded-full bg-green-500 p-1 text-white hover:bg-green-600"
                      onClick={handleDoneClick}
                    >
                      <Check size={20} />
                    </ShadcnButton>
                  )}
                </>
              )}
          </div>
        </div>

        {addItemsToPlaylistStatus === "loading" ? (
          <div className="flex w-full flex-col items-start justify-center gap-4 py-8">
            {Array.from({ length: 6 }).map((e, index) => (
              <PlaylistItemCardSkeleton key={index} />
            ))}
          </div>
        ) : optimisticItems.length > 0 ? (
          <div className="cus-scrollbar-w-none w-full overflow-y-auto pb-20 pt-8">
            <div className="flex w-full flex-col items-start justify-center gap-4">
              {optimisticItems.map((item) => {
                const { id, title, authors, progress, fullImageUrl } = item;

                const isLocked = lockedContentsObj?.[id!]?.isLocked;

                return (
                  <PlaylistItemCard
                    key={id}
                    id={id ?? ""}
                    title={title ?? ""}
                    author={authors?.[0] ?? ""}
                    image={fullImageUrl ?? ""}
                    progress={progress ?? 0}
                    isDraggMode={isDraggMode}
                    draggable={isDraggMode}
                    draggedItemId={draggedItemId?.toString()}
                    onClick={() => {
                      onSelectContent(
                        item,
                        {},
                        {
                          isLocked,
                          source: SelectedPlayerContentSource.Playlist,
                          nextItemSource: Api.Server.NextItemSource.Playlist,
                          playlistId: playlistId
                            ? Number(playlistId)
                            : undefined,
                        }
                      );

                      setDraggedItemId(id);
                    }}
                    onDragStart={(e) => handleDragStart(e, id)}
                    onDragOver={(e) => handleDragOver(e, id)}
                    onDragEnd={handleDragEnd}
                    isLocked={isLocked}
                  />
                );
              })}
            </div>
          </div>
        ) : (
          <EmptyPlaylist />
        )}
      </div>
    </div>
  );
};

export default PlayListPage;
